import React, { FC, FocusEvent, useEffect } from 'react';
import { TimeoutId } from '@reduxjs/toolkit/dist/query/core/buildMiddleware/types';
import { USER_TYPES } from '@sv-support-sdk/utils/src/constants/chat';
import { getNumberFromString } from '@sv-support-sdk/utils/src/helpers/string';
import { resetState } from '@sv-support-sdk/chat-store/src/modules/reset/actions';

import PositionWrap from './Layout/PositionWrap';
import Button from './ui/Controls/Button';
import ChatWrap from './Layout/Chat/ChatWrap';
import Chat from './Layout/Chat';
import MessagesList from './MessagesList';
import {
  chatBanSelector,
  chatIdSelector,
  chatIsMaintenanceSelector,
  chatIsOnlineSelector,
  chatOpenSelector,
  chatPopupSelector,
} from '../store/selectors/chat';
import { setPopup } from '../store/slices/chat';
import { initChat, setChatOpenState, sendChatLocation } from '../store/actions/chat';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import Modals from './Modals';
import NoticeDot from './ui/NoticeDot';

import '../styles/global.scss';
import { unreadMessagesSelector } from '../store/selectors/messages';
import { getCookie, setCookie } from '../helpers/cookies';
import { useIsMobile } from '../hooks/useIsMobile';
import { CHAT_CHECK_INTERVAL, CHAT_TOOLTIP_DELAY } from '../constants/chat';
import BanPage from './BanPage';
import { checkLastMessage, readMessage } from '../store/actions/messages';
import { closeSocketRoomConnect } from '../store/actions/socket';
import { IChatInit } from '../types/chat';
import { updateUser } from '../store/slices/user';
import ChatPopup from './ChatPopup';
import { $l } from '../locales';
import { POPUP_WAS_OPENED } from '../constants/storage';
import { selectUserId } from '../store/selectors/user';
import { setUnreadMessages } from '../store/slices/messages';
import { setInitData } from '../store/slices/initData';
import setQaAttribute from '../helpers/setQaAttribute';
import { SUPPORT_CHAT_BUTTON } from '../constants/qaAttributes';

import('../assets/svg/sprite.svg');

let chatCheckInterval: TimeoutId;
interface ChatInitData {
  chatInitData: IChatInit;
}

const App: FC<ChatInitData> = ({ chatInitData }) => {
  const chatOpenState = useAppSelector(chatOpenSelector);
  const hasUnreadMessages = useAppSelector(unreadMessagesSelector);
  const chatBanned = useAppSelector(chatBanSelector);
  const isChatOnline = useAppSelector(chatIsOnlineSelector);
  const chatId = useAppSelector(chatIdSelector);
  const isMaintenance = useAppSelector(chatIsMaintenanceSelector);
  const popup = useAppSelector(chatPopupSelector);
  const userId = useAppSelector(selectUserId);

  const dispatch = useAppDispatch();

  const isMobile = useIsMobile();

  const isIos = /iPhone|iPad|iPod/.test(window.navigator.userAgent);

  const openPopup = () => {
    const popupWasClosed = getCookie(POPUP_WAS_OPENED);
    const numberFromId = getNumberFromString(userId);

    if (!chatOpenState && (!popupWasClosed || popupWasClosed !== numberFromId)) {
      dispatch(setPopup(true));
    }
  };

  const closePopup = () => {
    setCookie(POPUP_WAS_OPENED, getNumberFromString(userId));
    dispatch(setPopup(false));
  };

  const pressChatButton = () => {
    closePopup();

    dispatch(setUnreadMessages(false));
    dispatch(setChatOpenState(!chatOpenState));
  };

  useEffect(() => {
    dispatch(setInitData(chatInitData));
  }, [chatInitData]);

  useEffect(() => {
    if (chatInitData?.user?.id && chatInitData?.user?.email) {
      dispatch(
        updateUser({
          ...chatInitData.user,
          type: USER_TYPES.REGISTERED,
        }),
      );
    }
    if (chatInitData.onChatRender) {
      chatInitData.onChatRender();
    }
    dispatch(initChat());

    const visibleChangeEvent = () => {
      if (document.visibilityState === 'visible') {
        dispatch(readMessage());
      }
    };

    document.addEventListener('visibilitychange', visibleChangeEvent);

    return () => {
      document.removeEventListener('visibilitychange', visibleChangeEvent);
    };
  }, [chatInitData]);

  useEffect(() => {
    if (chatInitData.onChatToggle) {
      chatInitData.onChatToggle(chatOpenState);
    }
  }, [chatOpenState]);

  const addMetaTag = () => {
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content = 'width=device-width,initial-scale=1,maximum-scale=1,user-scalable=0';
    meta.id = 'no-scale-meta';
    document.head.appendChild(meta);
  };

  const removeMetaTag = () => {
    const meta = document.getElementById('no-scale-meta');
    if (meta) {
      document.head.removeChild(meta);
    }
  };

  useEffect(() => {
    if (!isMobile || !isIos) return;

    if (chatOpenState) {
      addMetaTag();
    } else {
      removeMetaTag();
    }
  }, [chatOpenState, isMobile, isIos]);

  useEffect(() => {
    let previousLocation = window.location.href;

    const handleLocationChange = () => {
      if (previousLocation !== window.location.href) {
        previousLocation = window.location.href;
        dispatch(sendChatLocation({ url: window.location.href }));
      }
    };

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      const result = originalPushState.apply(this, args);
      window.dispatchEvent(new Event('pushstate'));
      window.dispatchEvent(new Event('locationchange'));
      return result;
    };

    history.replaceState = function (...args) {
      const result = originalReplaceState.apply(this, args);
      window.dispatchEvent(new Event('replacestate'));
      window.dispatchEvent(new Event('locationchange'));
      return result;
    };

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('pushstate', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('pushstate', handleLocationChange);
    };
  }, []);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => {
      openPopup();
    }, CHAT_TOOLTIP_DELAY);

    return () => {
      clearTimeout(timerId);
    };
  }, []);

  useEffect(() => {
    if (chatId) {
      clearInterval(chatCheckInterval);
      chatCheckInterval = setInterval(() => {
        dispatch(checkLastMessage());
      }, CHAT_CHECK_INTERVAL);
    } else if (!chatId) {
      clearInterval(chatCheckInterval);
      dispatch(closeSocketRoomConnect());
    }
  }, [chatId]);

  const buttonIcon = chatOpenState ? '24--arrow--down' : '24--question-mark';

  const onFocus = (event: FocusEvent<HTMLDivElement>) => {
    if (['textarea', 'input'].includes((event.target as HTMLElement).tagName.toLowerCase())) {
      event.stopPropagation();
    }
  };

  return (
    <PositionWrap
      bottom={isMobile ? '16' : '20'}
      right={isMobile ? '16' : '20'}
      onFocus={onFocus}
      className="globalChatStyles"
    >
      <ChatWrap>
        {popup && !chatOpenState && !isMobile && (
          <ChatPopup closeAction={closePopup}>{$l('CHAT_POPUP')}</ChatPopup>
        )}
        {!chatOpenState && hasUnreadMessages && <NoticeDot size="big" placement="top-rigth" />}
        {!chatInitData.options?.hideButton && (
          <Button
            zIndex="1500"
            onClick={pressChatButton}
            icon={buttonIcon}
            iconSize="24"
            radius="circle"
            shape="square"
            {...setQaAttribute(SUPPORT_CHAT_BUTTON)}
          />
        )}
        {chatOpenState ? (
          <Chat hideControls={!isChatOnline || chatBanned || isMaintenance} isMobile={isMobile}>
            {chatBanned ? <BanPage /> : <MessagesList />}
            <Modals />
          </Chat>
        ) : null}
      </ChatWrap>
    </PositionWrap>
  );
};

export default App;
